import { parseISO } from 'date-fns'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { IconButton, PrimaryButton, SecondaryButton } from '../../components/Button'
import FileInput from '../../components/FileInput'
import { InputLabel, OptionalLabel, TextInputWithLabel } from '../../components/Input'
import PageHeader from '../../components/PageHeader'
import SearchMultiselect from '../../components/SearchMultiselect'
import Close from '../../icons/Close'
import Edit from '../../icons/Edit'
import { AccountServicesType, Ubo, VerificationRequirement } from '../../state/Radom'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, highlightInvalidFields, sanitizeAddress, sanitizeFirstName } from '../../util/Util'
import { AddressForm } from './AddressForm'
import { IndividualGovernmentIdTypes, IndividualTaxNumberTypes } from './TaxNumberTypes'
import Checkbox from '../../components/Checkbox'
import Tooltip from '../../components/Tooltip'
import { Countries } from './Countries'
import PDFFileUpload from './PDFFileUpload'
import DateSelector2 from '../../components/DateSelector2'
import { VerificationRequirementError } from '../../components/VerificationRequirementError'

export interface UboForm extends Ubo {
  govIdImageFile?: File
  govIdImageUrl?: string
  lastUploadedGovIdImageFileName?: string
  proofOfAddressFile?: File
  proofOfAddressUrl?: string
  lastUploadedProofOfAddressFileName?: string
}

interface IProps {
  state: {
    ubos: UboForm[]
    ubo: UboForm
  }
  accountType: AccountServicesType
  isSubmitting: boolean
  canSubmit: boolean
  onUpdate: (value: any) => void
  onAdd: (ubo: UboForm) => Promise<void>
  onEdit: (index: number) => void
  onStopEdit: () => void
  onRemove: (index: number) => void
  onSubmit: () => void
  requirements: VerificationRequirement[]
}

const ListItem = styled.div<{ hasError?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  border: 1px solid ${({ hasError }) => hasError ? RADOM_COLORS.ERROR : RADOM_COLORS.GRAY8};
  border-radius: 10px;
  display: flex;
  list-style: none;
  margin: 16px 0;
  padding: 12px 20px;
  gap: 16px;
  background-color: ${({ hasError }) => hasError ? '#FFF5F5' : 'transparent'};

  &:first-child {
    margin-top: 0;
  }
    
  &:last-child {
    margin-bottom: 0;
  }
`

const StickingFooter = styled.div`
  position: fixed;
  padding: 20px;
  width: 100vw;
  background-color: white;
  border-top: 1px solid ${RADOM_COLORS.GRAY_DARK};
  top: calc(100vh - 80px);
  left: 0;
`

export const isUboComplete = (ubo: Ubo | UboForm, type: AccountServicesType): boolean => {
  const hasGovId = !!ubo.govIdImageUrl || !!(ubo as any).govIdImageFile || (typeof ubo.govIdImage === 'string' && ubo.govIdImage.length > 0)

  const hasBasic = !!ubo.firstName && !!ubo.lastName && !!ubo.birthDate &&
    !!ubo.email && !!ubo.phone && hasGovId && !!ubo.govIdExpirationDate

  if (type !== AccountServicesType.crypto_and_fiat) {
    return hasBasic
  }

  const hasPoa = !!ubo.proofOfAddressUrl || !!(ubo as any).proofOfAddressFile || (typeof ubo.proofOfAddress === 'string' && ubo.proofOfAddress.length > 0)

  return hasBasic &&
    !!ubo.residentialAddress.city &&
    !!ubo.residentialAddress.country &&
    !!ubo.taxIdentificationNumber &&
    !!ubo.relationshipEstablishedAt &&
    hasPoa
}

const Ubos = ({ accountType, state, isSubmitting, canSubmit, requirements, ...props }: IProps): ReactElement => {
  const [ubo, setUbo] = useState<UboForm>(state.ubo)
  const [isEditing, setIsEditing] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number>(-1)

  useEffect(() => {
    if (JSON.stringify(state.ubo) !== JSON.stringify(ubo)) {
      setUbo(state.ubo)
    }
  }, [state.ubo])

  useEffect(() => {
    if (JSON.stringify(state.ubo) !== JSON.stringify(ubo)) {
      const handler = setTimeout(() => {
        props.onUpdate(ubo)
      }, 100)

      return () => clearTimeout(handler)
    }
  }, [ubo])

  const allUbosValid = useMemo(() => {
    return state.ubos.length > 0 && state.ubos.every(u => isUboComplete(u, accountType))
  }, [state.ubos, accountType])

  const canSave = useMemo(() => {
    return isUboComplete(ubo, accountType)
  }, [ubo, accountType])

  useEffect(() => {
    setUbo(state.ubo)
  }, [state.ubo])

  const getUboError = (field: string): string | undefined => {
    if (editingIndex === -1) return undefined
    const key = `associatedPersons[${editingIndex}].${field}`
    return requirements.find(r => r.fieldId === key)?.comment
  }

  const hasUboAnyError = (index: number): boolean => {
    return requirements.some(r => r.fieldId.startsWith(`associatedPersons[${index}]`))
  }
  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>, property: 'govIdImageFile' | 'proofOfAddressFile'): Promise<void> => {
    const fileList = event.target.files

    switch (property) {
    case 'govIdImageFile':
      if (fileList && fileList.length > 0 && fileList[0].type.startsWith('image')) {
        setUbo({ ...ubo, [property]: fileList[0] })
      } else {
        errorToast('Invalid file type. Please upload an image file.')
      }
      break

    case 'proofOfAddressFile':
      if (fileList && fileList.length > 0 && fileList[0].type === 'application/pdf') {
        setUbo({ ...ubo, [property]: fileList[0] })
      } else {
        errorToast('Invalid file type. Please upload a PDF file.')
      }
    }
  }

  const onSave = (): void => {
    props.onAdd(ubo)
      .then(() => {
        setIsEditing(false)
      }).catch(() => {})
  }

  const renderUboForm = (): ReactElement => {
    return (<div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: '100px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>Edit associated person details</span>
        <IconButton onClick={() => {
          setIsEditing(false)
          props.onStopEdit()
        }}>
          <Close style={{ height: 16, width: 'auto', pointerEvents: 'none', stroke: RADOM_COLORS.BLACK }} />
        </IconButton>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <span style={{ fontSize: 18 }}>Basic details</span>

          <TextInputWithLabel
            label="First name"
            required
            tooltip="Given name"
            placeholder="Enter first name"
            value={ubo.firstName}
            errorMessage={getUboError('firstName')}
            onChange={(e) => {
              const eventTarget = e.target as HTMLInputElement
              setUbo({ ...ubo, firstName: sanitizeFirstName(eventTarget.value) })
            }}
          />
          <TextInputWithLabel
            label="Last name"
            required
            tooltip="Family name"
            placeholder="Enter last name"
            errorMessage={getUboError('lastName')}
            value={ubo.lastName}
            onChange={(e) => {
              const eventTarget = e.target as HTMLInputElement
              setUbo({ ...ubo, lastName: sanitizeFirstName(eventTarget.value) })
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <InputLabel>Birth date</InputLabel>
              <Tooltip message='Date of birth' />
            </div>
            <VerificationRequirementError message={getUboError('birthDate')} />
            <DateSelector2
              onChange={(date) => setUbo({ ...ubo, birthDate: date?.toISOString().split('T')[0] })}
              value={ubo.birthDate ? parseISO(ubo.birthDate) : undefined}
              startDate={new Date('1900-01-01')}
              endDate={new Date()}
              placeholder='DD/MM/YYYY'
            />
          </div>
          <VerificationRequirementError
            message={[getUboError('email'), getUboError('phone')]
              .filter(Boolean)
              .join(' ')}
          />
          <div style={{ display: 'flex', gap: 20 }}>
            <TextInputWithLabel
              style={{ flexGrow: 1 }}
              label="Email"
              required
              tooltip="Contact email"
              placeholder="Enter email address"
              value={ubo.email}
              onChange={(e) => {
                const eventTarget = e.target as HTMLInputElement
                setUbo({ ...ubo, email: eventTarget.value })
              }}
            />
            <TextInputWithLabel
              style={{ flexGrow: 1 }}
              label="Phone"
              required
              tooltip="Contact phone number"
              placeholder="Enter phone number"
              value={ubo.phone}
              onChange={(e) => {
                const eventTarget = e.target as HTMLInputElement
                setUbo({ ...ubo, phone: eventTarget.value })
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <InputLabel>Government ID type</InputLabel>
              <Tooltip message='Select ID document type' />
            </div>
            <VerificationRequirementError message={getUboError('govIdImageType')} />
            <SearchMultiselect
              isSingleSelect
              placeholder="Select government ID type"
              dropdownPlaceholder="No government ID types found"
              itemArray={IndividualGovernmentIdTypes.map(a => ({ key: a.description, value: a.value }))}
              selectedItems={ubo.govIdImageType
                ? [{
                  key: (IndividualGovernmentIdTypes as any).find((a: any) => a.value === ubo.govIdImageType)?.description ?? ubo.govIdImageType,
                  value: ubo.govIdImageType
                }]
                : []}
              setSelectedItems={(items) => {
                const selected = items[0]
                if (selected) {
                  setUbo({ ...ubo, govIdImageType: selected.value })
                }
              }}
              keyExtractor={(country) => country.value}
              labelExtractor={(country) => country.key}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <InputLabel>Government ID country</InputLabel>
              <Tooltip message='Issuing country of the ID' />
            </div>
            <VerificationRequirementError message={getUboError('govIdImageCountry')} />
            <SearchMultiselect
              isSingleSelect
              placeholder="Select country"
              dropdownPlaceholder="No countries found"
              itemArray={Countries.map(s => ({ key: s.name, value: s.alpha3 }))}
              selectedItems={ubo.govIdImageCountry
                ? [{
                  key: Countries.find(s => s.alpha3 === ubo.govIdImageCountry)?.name,
                  value: ubo.govIdImageCountry
                }]
                : []}
              setSelectedItems={(items) => {
                const selected = items[0]
                if (selected) {
                  setUbo({ ...ubo, govIdImageCountry: selected.value })
                }
              }}
              keyExtractor={(country) => country.value}
              labelExtractor={(country) => country.key}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <InputLabel>
                Government ID
              </InputLabel>
              <OptionalLabel>Image</OptionalLabel>
              <Tooltip message='Upload a government issued identification document' />
            </div>
            <VerificationRequirementError message={getUboError('govIdImage')} />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <FileInput
                value={ubo.govIdImageFile}
                onChange={async (evt) => await onFileChange(evt, 'govIdImageFile')}
              />
              {ubo.govIdImageUrl && (
                <img src={ubo.govIdImageUrl} alt="Government ID" style={{ width: 'auto', height: 70, borderRadius: 5 }} />
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <InputLabel>Government ID expiration date</InputLabel>
              <Tooltip message='Government ID expiration date' />
            </div>
            <VerificationRequirementError message={getUboError('govIdExpirationDate')} />
            <DateSelector2
              onChange={(date) => setUbo({ ...ubo, govIdExpirationDate: date?.toISOString().split('T')[0] })}
              value={ubo.govIdExpirationDate ? parseISO(ubo.govIdExpirationDate) : undefined}
              startDate={new Date()}
              endDate={new Date(new Date().getFullYear() + 100, 11, 31)}
              initialDate={new Date()}
              placeholder='DD/MM/YYYY'
            />
          </div>
        </div>
        { accountType === AccountServicesType.crypto_and_fiat && <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span style={{ fontSize: 18 }}>Residental address</span>
            <VerificationRequirementError
              message={[
                getUboError('residentialAddress.streetLine1'),
                getUboError('residentialAddress.streetLine2'),
                getUboError('residentialAddress.city'),
                getUboError('residentialAddress.postalCode'),
                getUboError('residentialAddress.country')
              ]
                .filter(Boolean)
                .join(' ')}
            />
            <AddressForm state={ubo.residentialAddress} onUpdate={(t) => {
              const sanitizedStreetLine = sanitizeAddress(t.streetLine1 || '')
              const sanitizedStreetLine2 = sanitizeAddress(t.streetLine2 || '')
              const sanitizedCity = sanitizeAddress(t.city || '')
              const sanitizedPostalCode = sanitizeAddress(t.postalCode || '')
              setUbo({
                ...ubo,
                residentialAddress:
                      {
                        ...t,
                        streetLine1: sanitizedStreetLine,
                        streetLine2:
                        sanitizedStreetLine2,
                        city: sanitizedCity,
                        postalCode: sanitizedPostalCode
                      }
              })
            }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <InputLabel>
              Proof of address
                </InputLabel>
                <OptionalLabel>PDF</OptionalLabel>
                <Tooltip message='Document proving residence, such as a utility bill' />
              </div>
              <VerificationRequirementError message={getUboError('proofOfAddress')} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{
                  border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                  padding: 20,
                  background: RADOM_COLORS.GRAY9,
                  borderRadius: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10
                }}>
                  <span>Upload one of the following issued to this associated person:</span>
                  <ul style={{ paddingLeft: 30, margin: 0 }}>
                    <li><b>Utility bill</b> (less than 90 days old)</li>
                    <li><b>Bank statement</b> (less than 90 days old)</li>
                    <li><b>Residential lease agreement</b> (may be older than 90 days)</li>
                  </ul>
                </div>
                <PDFFileUpload
                  file={ubo.proofOfAddressUrl ?? ubo.proofOfAddressFile}
                  onFile={f => setUbo({ ...ubo, proofOfAddressFile: f })}
                  onRemove={() => {
                    setUbo({
                      ...ubo,
                      proofOfAddress: '',
                      proofOfAddressUrl: undefined,
                      proofOfAddressFile: undefined,
                      lastUploadedProofOfAddressFileName: undefined
                    })
                  }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span style={{ fontSize: 18 }}>Legal details</span>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <InputLabel>Tax identification number type</InputLabel>
                <Tooltip message='Select the individuals tax ID type' />
              </div>
              <SearchMultiselect
                isSingleSelect
                placeholder="Select tax number type"
                dropdownPlaceholder="No tax number types found"
                itemArray={
                  IndividualTaxNumberTypes[ubo.residentialAddress.country]
                    ?.map(a => ({ key: a.description, value: a.value })) ?? []
                }
                selectedItems={ubo.taxIdentificationNumberType
                  ? [{
                    key: (ubo.residentialAddress?.country && ((IndividualTaxNumberTypes as any)[ubo.residentialAddress.country] as any)?.find((a: any) => a.value === ubo.taxIdentificationNumberType)?.description) ?? ubo.taxIdentificationNumberType,
                    value: ubo.taxIdentificationNumberType
                  }]
                  : []}
                setSelectedItems={(items) => {
                  const selected = items[0]
                  if (selected) {
                    setUbo({ ...ubo, taxIdentificationNumberType: selected.value })
                  }
                }}
                keyExtractor={(country) => country.value}
                labelExtractor={(country) => country.key}
              />
            </div>

            <TextInputWithLabel
              label="Tax identification number"
              required
              tooltip="Enter the individual's tax ID number"
              placeholder="Enter tax number"
              errorMessage={getUboError('taxIdentificationNumber')}
              value={ubo.taxIdentificationNumber}
              onChange={(e) => {
                const eventTarget = e.target as HTMLInputElement
                setUbo({ ...ubo, taxIdentificationNumber: eventTarget.value })
              }}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <InputLabel>
                Has ownership
                </InputLabel>
                <Tooltip message='True if this person has at least 25% ownership of the business.' />
              </div>
              <VerificationRequirementError message={getUboError('hasOwnership')} />
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
                onClick={() => setUbo({ ...ubo, hasOwnership: !ubo.hasOwnership })}>
                <Checkbox checked={ubo.hasOwnership} />
                <span>Has at least 25% ownership of the business</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <InputLabel>
                Has control
                </InputLabel>
                <Tooltip message='True if this is the control person of the company, having significant responsibility to control, manage or influence the activities of the business entity. At least one control person must be specified.' />
              </div>
              <VerificationRequirementError message={getUboError('hasControl')} />
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
                onClick={() => setUbo({ ...ubo, hasControl: !ubo.hasControl })}>
                <Checkbox checked={ubo.hasControl} />
                <span>Is the control person of the company</span>
              </div>
            </div>

            {
              ubo.hasControl &&
            <TextInputWithLabel
              label="Title"
              tooltip='The title of this beneficial owner at the company, e.g. CEO, CFO, etc.'
              required
              placeholder="Enter title"
              value={ubo.title}
              errorMessage={getUboError('title')}
              onChange={(e) => {
                const eventTarget = e.target as HTMLInputElement
                setUbo({ ...ubo, title: eventTarget.value })
              }}
            />
            }

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <InputLabel>
                Is signer
                </InputLabel>
                <Tooltip message='True if this person is able to authorize transactions on behalf of the business. At least one signer must be specified.' />
              </div>
              <VerificationRequirementError message={getUboError('isSigner')} />
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
                onClick={() => setUbo({ ...ubo, isSigner: !ubo.isSigner })}>
                <Checkbox checked={ubo.isSigner} />
                <span>Is able to authorize transactions on behalf of the business</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <InputLabel>Join date</InputLabel>
              <VerificationRequirementError message={getUboError('relationshipEstablishedAt')} />
              <DateSelector2
                onChange={(date) => setUbo({
                  ...ubo,
                  relationshipEstablishedAt: date?.toISOString().split('T')[0]
                })}
                value={ubo.relationshipEstablishedAt ? parseISO(ubo.relationshipEstablishedAt) : undefined}
                startDate={new Date('1900-01-01')}
                endDate={new Date()}
                placeholder='DD/MM/YYYY'
              />
            </div>

          </div>
        </>}
      </div>
    </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 14 }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Ownership structure" subTitle="Introduce the key individuals who own or control your business" />
      </div>
      {!isEditing && state.ubos.length > 0 && !allUbosValid && (
        <div style={{ color: RADOM_COLORS.ERROR, padding: '15px', border: `1px solid ${RADOM_COLORS.RED300}`, borderRadius: '5px', backgroundColor: 'rgba(255,0,0,0.05)' }}>
          <strong>Action Required:</strong> You have switched your account type. Please edit your existing
          Associated Persons to add missing details (Address, Tax ID, etc.) required for Crypto & Fiat.
        </div>
      )}
      {state.ubos.length > 0 && !isEditing && (
        <div style={{ width: '100%', padding: 0 }}>
          {state.ubos.map((ubo, index) => (
            <ListItem key={ubo.email} hasError={hasUboAnyError(index)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span>{ubo.firstName} {ubo.lastName}</span>
                <span style={{ fontSize: '12px', color: RADOM_COLORS.GRAY4 }}>{ubo.email}</span>
                <span style={{ fontSize: '12px', color: RADOM_COLORS.GRAY4 }}>
                  {
                    [
                      ubo.residentialAddress.streetLine1,
                      ubo.residentialAddress.city,
                      ubo.residentialAddress.subdivision,
                      ubo.residentialAddress.postalCode,
                      ubo.residentialAddress.country
                    ].join(' ')
                  }
                </span>
                {hasUboAnyError(index) && (
                  <span style={{ color: RADOM_COLORS.ERROR, fontSize: '12px', marginTop: 4 }}>
                    Action Required: Please edit to view details
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <IconButton style={{ padding: 0 }} onClick={() => {
                  setIsEditing(true)
                  setEditingIndex(index)
                  props.onEdit(index)
                }}>
                  <Edit style={{ height: 16, width: 'auto', pointerEvents: 'none', stroke: RADOM_COLORS.BLACK }} />
                </IconButton>
                <IconButton style={{ padding: 0 }} onClick={() => props.onRemove(index)}>
                  <Close style={{ height: 16, width: 'auto', pointerEvents: 'none', stroke: RADOM_COLORS.BLACK }} />
                </IconButton>
              </div>
            </ListItem>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, overflow: 'scroll' }}>
        {isEditing
          ? renderUboForm()
          : (
            <SecondaryButton onClick={() => {
              setIsEditing(true)
              setEditingIndex(-1)
            }}>
              Add associated person
            </SecondaryButton>
          )}
      </div>
      {isEditing
        ? (
          <StickingFooter>
            <PrimaryButton
              disabled={isSubmitting}
              onClick={() => {
                if (canSave) {
                  onSave()
                } else {
                  highlightInvalidFields()
                }
              }}>
              Save
            </PrimaryButton>
          </StickingFooter>
        )
        : (
          <PrimaryButton
            disabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={() => {
              if (allUbosValid && canSubmit) {
                props.onSubmit()
              } else {
                highlightInvalidFields()
                if (!allUbosValid) errorToast('Please complete details for all associated persons.')
              }
            }}>
            Continue
          </PrimaryButton>
        )}
    </div>
  )
}

export default Ubos
